/*
  Beyond the Fridge — Create Your Own board tool (sections/btf-cyo-tool.liquid).

  The geometry, crop model, bleed maths and resolution check are lifted verbatim from the
  prototype logic in design_handoff_shopify_horizon/designs/Create Your Own - Board Concept.dc.html
  (desktop) and Create Your Own - Board Mobile.dc.html (mobile). This file adds what a shop needs
  around them: sessionStorage + IndexedDB persistence, the upload pipeline, the add-to-basket form
  and Horizon's cart events.

  Loaded as an ES module so it can import Horizon's cart event class through the theme importmap.
*/

// Older Safari (iOS 15) has no container units: assets/btf-cq.js sets them for the rules that use them
if (!(window.CSS && CSS.supports && CSS.supports('width: 1cqw'))) {
  import((window.btfAssets && window.btfAssets.cq) || './btf-cq.js').then((m) => m.containerUnits('.btf-cyo__empty')).catch(() => {});
}

const CM = {
  Landscape: { Small: [55, 40], Medium: [75, 55], Large: [100, 75] },
  Portrait: { Small: [40, 55], Medium: [55, 75], Large: [75, 100] },
};
const PRICE = { Small: 85, Medium: 110, Large: 160 };
const KEY = { Landscape: 'land', Portrait: 'port' };
const SZK = { Small: 'sm', Medium: 'md', Large: 'lg' };

// Board rectangle inside each source canvas, measured from the alpha of the supplied PNGs.
const PROD = {
  Landscape: {
    ar: '1000 / 730',
    arNum: 1000 / 730,
    Large: [4.3, 2.9, 90.7, 93.2],
    Medium: [15.8, 15.6, 68.3, 68.6],
    Small: [25.0, 25.1, 49.9, 49.6],
  },
  Portrait: {
    ar: '1 / 1',
    arNum: 1,
    Large: [16.0, 4.3, 68.0, 90.6],
    Medium: [25.0, 15.8, 49.7, 68.3],
    Small: [31.6, 21.0, 36.2, 49.9],
  },
};
const ROOM = {
  Landscape: { Large: [27.0, 9.6, 47.5, 35.6], Medium: [32.5, 18.9, 35.3, 25.7], Small: [36.5, 20.2, 31.2, 22.6] },
  Portrait: { Large: [33.4, 4.7, 35.2, 47.0], Medium: [36.8, 14.3, 25.7, 35.3], Small: [40.5, 16.0, 22.6, 31.2] },
};
const SIZES = ['Small', 'Medium', 'Large'];
const ORIENTS = ['Landscape', 'Portrait'];

// Quality panel grounds and rules per state: awaiting, good, fair, floor, too small.
const QUALITY = {
  empty: ['#f3f2f2', '#c9c6c3'],
  good: ['#eef4ee', '#2f6b3a'],
  fair: ['#f4f6f2', '#5b7a4a'],
  floor: ['#fdf6e3', '#a8791a'],
  bad: ['#fdeceb', '#e53712'],
};

const STORE_KEY = 'btf-cyo';
const PREVIEW_MAX = 2400; // px on the long edge for the on-screen preview; the original file is what uploads
const mobileQuery = window.matchMedia('(max-width: 749px)');
const isMobile = () => mobileQuery.matches;
const clamp = (v) => Math.max(-1, Math.min(1, v));
const n = (v) => Number(v).toLocaleString('en-GB');

/* --------------------------------------------------------------------------
   IndexedDB: keeps the original file across a refresh so the crop state in
   sessionStorage can be restored against the same photograph.
   -------------------------------------------------------------------------- */
const DB_NAME = 'btf-cyo';
const DB_STORE = 'files';

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') return reject(new Error('no indexedDB'));
    const rq = indexedDB.open(DB_NAME, 1);
    rq.onupgradeneeded = () => rq.result.createObjectStore(DB_STORE);
    rq.onsuccess = () => resolve(rq.result);
    rq.onerror = () => reject(rq.error);
  });
}

function dbRun(mode, fn) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(DB_STORE, mode);
        const rq = fn(tx.objectStore(DB_STORE));
        tx.oncomplete = () => resolve(rq && rq.result);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      })
  );
}

const idbPut = (blob) => dbRun('readwrite', (store) => store.put(blob, 'current'));
const idbGet = () => dbRun('readonly', (store) => store.get('current'));
const idbDel = () => dbRun('readwrite', (store) => store.delete('current'));

/* --------------------------------------------------------------------------
   XHR post so the customer sees real upload progress on a 10–25 MB file.
   -------------------------------------------------------------------------- */
function postForm(url, body, onProgress, headers) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Accept', 'application/json');
    Object.keys(headers || {}).forEach((k) => xhr.setRequestHeader(k, headers[k]));
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () => {
      let json = null;
      try {
        json = JSON.parse(xhr.responseText);
      } catch (e) {
        json = null;
      }
      resolve({ status: xhr.status, json, text: xhr.responseText });
    };
    xhr.onerror = () => reject(new Error('The connection dropped during the upload.'));
    xhr.ontimeout = () => reject(new Error('The upload timed out.'));
    xhr.send(body);
  });
}

/* --------------------------------------------------------------------------
   How the board reads on the order in Shopify admin. A file attached to a
   cart line shows there only as a small thumbnail, so once the line is in the
   basket its properties are rewritten in a set order, with the files as plain
   links (as the old upload app showed them) and the print details in words.
   The old key names are still read, for boards already in baskets.
   -------------------------------------------------------------------------- */
// The order line, top first: the artwork to print, the customer's original, and when they
// approved it (Duncan, 19 Oct 2026). The basket shows the artwork itself, made small by the CDN.
// Same names as assets/btf-print-jpeg.js.
const PRINT_FILE = '_Artwork (print from this)';
const APPROVED = 'Approved for print';
const ORIGINAL = '_Original file';
const OLD_ORIGINALS = ["_Customer's original (backup)", '_Original file (print from this)'];
const ORDER_KEYS = [PRINT_FILE, ORIGINAL, APPROVED, 'Screw caps', 'PO reference', 'Proof', '_Print size', '_Resolution', '_File missing', '_Original file name', '_Crop from original'];
const printJpegModule = () => import((window.btfAssets && window.btfAssets.printJpeg) || './btf-print-jpeg.js');

function artworkUrl(props) {
  const v = props && [ORIGINAL, ...OLD_ORIGINALS, '_artwork_url'].map((k) => props[k]).find(Boolean);
  return String(v || '');
}

// A file property's full-size address, as a link that downloads the JPEG itself
function fileLink(value) {
  const urls = String(value).match(/https?:\/\/[^"',\s\]]+/g) || [];
  const full = urls.find((u) => !/_small\.[a-z]+$/i.test(u)) || urls[0];
  if (!full) return String(value);
  return /\.jpe?g$/i.test(full) ? full + '?format=pjpg' : full;
}

function readable(g, s) {
  const bx = 500 / g.wmm;
  const by = 500 / g.hmm;
  const toX = (p) => Math.round(((p - g.left) / g.dw) * s.nw);
  const toY = (p) => Math.round(((p - g.top) / g.dh) * s.nh);
  const k = [toX(0), toY(0), toX(100) - toX(0), toY(100) - toY(0)];
  const b = [toX(-bx), toY(-by), toX(100 + bx) - toX(-bx), toY(100 + by) - toY(-by)];
  return {
    size: `${s.size} ${s.orient.toLowerCase()} · ${g.wcm} × ${g.hcm} cm finished · print ${g.bwmm} × ${g.bhmm} mm (5 mm bleed each edge)`,
    crop: s.nw
      ? `From the ${n(s.nw)} × ${n(s.nh)} px original, use ${n(b[2])} × ${n(b[3])} px starting ${n(b[0])} across, ${n(b[1])} down (the board without bleed: ${n(k[2])} × ${n(k[3])} px starting ${n(k[0])}, ${n(k[1])}).`
      : '',
  };
}

// Rewrites the line just added: same values, files as links, in the order above. A failure
// leaves the line exactly as it was added, which is still complete.
async function tidyLine(item, changeUrl) {
  if (!item || !item.key || !item.properties) return;
  const props = item.properties;
  const out = {};
  for (const key of ORDER_KEYS) if (props[key] != null && props[key] !== '') out[key] = key === PRINT_FILE ? fileLink(props[key]) : String(props[key]);
  for (const key of Object.keys(props)) if (!(key in out) && props[key] != null && props[key] !== '') out[key] = String(props[key]);
  try {
    await fetch(changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: out }),
    });
  } catch (e) {
    /* the line stands as added */
  }
}

function setOn(el, on) {
  if (!el) return;
  el.classList.toggle('is-on', on);
  el.setAttribute('aria-pressed', on ? 'true' : 'false');
}

class BtfCyoTool extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';

    const cfgEl = this.querySelector('script[data-btf-cyo-config]');
    let cfg = {};
    try {
      cfg = cfgEl ? JSON.parse(cfgEl.textContent) : {};
    } catch (e) {
      cfg = {};
    }
    this.cfg = cfg;
    this.refs = {};
    this.querySelectorAll('[data-ref]').forEach((el) => {
      this.refs[el.dataset.ref] = el;
    });

    this.state = {
      orient: ORIENTS.includes(cfg.startOrientation) ? cfg.startOrientation : 'Landscape',
      // Opens as every board does (Duncan, 8 Oct 2026): the large board drawn, no size ticked and
      // "From £85" until the customer picks one; Add to basket asks for a size first
      size: 'Large',
      sizeChosen: false,
      cap: 'White',
      view: 'product',
      src: null,
      nw: 0,
      nh: 0,
      zoom: 1,
      fx: 0,
      fy: 0,
      si: 0,
      dragging: false,
      srcKind: null, // 'file' | 'sample'
      srcIndex: -1,
      srcName: '',
      file: null,
      busy: false,
      progress: -1,
      status: '',
      error: '',
    };

    this.persistTimer = 0;
    this.bind();
    this.render();
    this.restore();
    mobileQuery.addEventListener('change', () => this.render());
  }

  /* ------------------------------------------------------------ geometry */

  geom() {
    const s = this.state;
    const [wcm, hcm] = CM[s.orient][s.size];
    const room = s.view === 'room';
    const k = KEY[s.orient] + '-' + SZK[s.size];
    const cap = s.cap === 'White' ? 'white' : 'black';
    const pr = PROD[s.orient];
    const rect = room ? ROOM[s.orient][s.size] : pr[s.size];
    const has = !!s.src;

    // Crop is expressed entirely in percentages of the board box, so it is identical
    // in the product and room views regardless of how large each renders.
    const A = s.nw && s.nh ? s.nw / s.nh : 1;
    const R = wcm / hcm;
    // 5 mm bleed on every edge: the file must cover the finished size plus 10 mm overall
    // on each dimension, so the photo layer is grown past the visible plate.
    const wmm = wcm * 10;
    const hmm = hcm * 10;
    const bwmm = wmm + 10;
    const bhmm = hmm + 10;
    const dw = (A > R ? A / R : 1) * 100 * s.zoom * (bwmm / wmm);
    const dh = (A > R ? 1 : R / A) * 100 * s.zoom * (bhmm / hmm);
    const left = (100 - dw) / 2 + (s.fx * (dw - 100)) / 2;
    const top = (100 - dh) / 2 + (s.fy * (dh - 100)) / 2;

    const cropW = Math.round((s.nw * 100) / dw);
    const cropH = Math.round((s.nh * 100) / dh);
    const dpi = has ? cropW / (bwmm / 25.4) : 0;
    const reqW = Math.ceil((bwmm / 25.4) * 150);
    const reqH = Math.ceil((bhmm / 25.4) * 150);
    // Below the shop's minimum (a section setting) the board cannot go in the basket: the
    // studio can enhance a soft file, but not one this far short.
    const good = dpi >= 150;
    const fair = dpi >= 75;
    const floor = dpi >= (Number(this.cfg.minDpi) || 50);
    const tier = !has ? 'empty' : good ? 'good' : fair ? 'fair' : floor ? 'floor' : 'bad';

    return { wcm, hcm, room, k, cap, pr, rect, has, wmm, hmm, bwmm, bhmm, dw, dh, left, top, cropW, cropH, dpi, reqW, reqH, tier };
  }

  // Below the minimum: what the customer can do about it, naming a size that would work.
  tooSmall(g) {
    const s = this.state;
    const keep = s.size;
    const sizes = ['Small', 'Medium', 'Large'];
    let fits = '';
    for (const size of sizes.slice(0, sizes.indexOf(keep)).reverse()) {
      s.size = size;
      const t = this.geom().tier;
      if (t !== 'bad') {
        fits = size;
        break;
      }
    }
    s.size = keep;
    const [w, h] = CM[s.orient][fits || keep];
    return fits
      ? `Too few pixels to print well at this size. Choose the ${fits.toLowerCase()} board (${w} × ${h} cm), crop out less, or upload a larger file.`
      : 'Too few pixels to print well on any of our boards. Crop out less, or upload a larger file — the original from your camera or phone, not one sent through a messaging app.';
  }

  texts(g) {
    const m = isMobile();
    const qTitle =
      g.tier === 'empty'
        ? 'Awaiting your file'
        : g.tier === 'good'
          ? 'Meets production standard'
          : g.tier === 'fair'
            ? 'Under 150 dpi — we enhance it'
            : g.tier === 'floor'
              ? 'Low resolution'
              : 'Too small for this board size';

    let qBody;
    if (g.tier === 'empty') {
      qBody = m
        ? 'We measure your file against the print size — the finished size plus 5 mm bleed on every edge — at our production standard of 150 dpi.'
        : 'We read the pixel size the moment it lands and measure it against the print size — the finished size plus 5 mm bleed on every edge — at our production standard of 150 dpi.';
    } else {
      const head = m
        ? `${n(g.cropW)} × ${n(g.cropH)} px across ${g.bwmm} × ${g.bhmm} mm — ${Math.round(g.dpi)} dpi. 150 dpi wants ${n(g.reqW)} × ${n(g.reqH)} px. `
        : `${n(g.cropW)} × ${n(g.cropH)} px across the ${g.bwmm} × ${g.bhmm} mm print size — ${Math.round(g.dpi)} dpi. 150 dpi wants ${n(g.reqW)} × ${n(g.reqH)} px. `;
      const tail =
        g.tier === 'good'
          ? 'Straight to press.'
          : g.tier === 'fair'
            ? 'It will print well; we enhance it to print size before printing.'
            : g.tier === 'floor'
              ? m
                ? 'Acceptable, but soft up close.'
                : 'Acceptable, but soft up close. Crop out less, or drop a board size.'
              : this.tooSmall(g);
      qBody = head + tail;
    }

    const dropNote =
      `JPEG, PNG or TIFF · ${Math.round((g.bwmm / 25.4) * 150)} × ${Math.round((g.bhmm / 25.4) * 150)} px for this ${g.wcm} × ${g.hcm} cm board` +
      (m ? '' : ' · we check the resolution for you');
    const cmLabel = `${g.wcm} × ${g.hcm} cm · print ${g.bwmm} × ${g.bhmm} mm`;
    const bleedNote = `Finished size ${g.wcm} × ${g.hcm} cm. Printed at ${g.bwmm} × ${g.bhmm} mm — 5 mm bleed off every edge — so the crop you see is the keep, not the whole file.`;
    return { qTitle, qBody, dropNote, cmLabel, bleedNote };
  }

  /* ------------------------------------------------------------ products */

  product(orient) {
    return (this.cfg.products && this.cfg.products[orient]) || null;
  }

  variantFor(orient, size) {
    const p = this.product(orient);
    return (p && p.variants && p.variants[size]) || null;
  }

  pricePence(size) {
    const v = this.variantFor(this.state.orient, size);
    return v && typeof v.price === 'number' ? v.price : PRICE[size] * 100;
  }

  fmtBig(p) {
    return '£ ' + (p / 100).toFixed(2);
  }

  fmtShort(p) {
    return '£' + (p % 100 === 0 ? String(p / 100) : (p / 100).toFixed(2));
  }

  /* -------------------------------------------------------------- render */

  render() {
    this.renderBoard();
    this.shareStrip();
  }

  renderBoard() {
    const s = this.state;
    const r = this.refs;
    const g = this.geom();
    const t = this.texts(g);
    const m = isMobile();
    const plates = this.cfg.plates || {};

    // Stage: the product plate is 1000 × 730 for landscape, square for portrait and the room.
    r.stage.style.width = m ? '100%' : `min(100%, ${Math.round(430 * (g.room ? 1 : g.pr.arNum))}px)`;
    r.stage.style.aspectRatio = g.room ? '1 / 1' : g.pr.ar;
    r.stage.classList.toggle('is-empty', !g.has);
    r.stage.classList.toggle('is-room', g.room);

    const clip = r.clip.style;
    clip.left = g.rect[0] + '%';
    clip.top = g.rect[1] + '%';
    clip.width = g.rect[2] + '%';
    clip.height = g.rect[3] + '%';
    // 35 mm corner radius on every board, so a different percentage on each size
    clip.borderRadius = (350 / g.wcm).toFixed(2) + '% / ' + (350 / g.hcm).toFixed(2) + '%';
    clip.cursor = g.has ? 'grab' : 'default';
    r.clip.tabIndex = g.has ? 0 : -1;

    const ph = r.photo.style;
    ph.left = g.left + '%';
    ph.top = g.top + '%';
    ph.width = g.dw + '%';
    ph.height = g.dh + '%';
    ph.backgroundImage = g.has ? `url("${s.src}")` : 'none';

    const overlay = g.room ? plates['room-' + g.k + '-room'] : plates['product-' + g.k + '-area'];
    const caps = plates[(g.room ? 'room-' : 'product-') + g.k + '-caps-' + g.cap];
    r.overlay.style.backgroundImage = overlay ? `url("${overlay}")` : 'none';
    r.caps.style.backgroundImage = caps ? `url("${caps}")` : 'none';

    r.empty.hidden = g.has;
    r.dash.classList.toggle('is-dragging', s.dragging);
    r.dropNote.textContent = t.dropNote;

    r.uploadLabel.textContent = g.has ? 'Replace photo' : 'Upload photo';
    r.cm.textContent = t.cmLabel;
    r.bleedNote.textContent = t.bleedNote;
    r.zoom.value = String(s.zoom);
    r.zoom.disabled = !g.has;
    r.fit.disabled = !g.has;

    const [qBg, qBd] = QUALITY[g.tier];
    r.quality.style.background = qBg;
    r.quality.style.borderColor = qBd;
    r.quality.dataset.tier = g.tier;
    r.qualityTitle.textContent = t.qTitle;
    r.qualityBody.textContent = s.error || t.qBody;

    setOn(r.viewProduct, !g.room);
    setOn(r.viewRoom, g.room);
    setOn(r.orientLand, s.orient === 'Landscape');
    setOn(r.orientPort, s.orient === 'Portrait');
    setOn(r.capWhite, s.cap === 'White');
    setOn(r.capBlack, s.cap === 'Black');

    this.querySelectorAll('[data-size]').forEach((b) => {
      const name = b.dataset.size;
      setOn(b, s.sizeChosen && s.size === name);
      const [w, h] = CM[s.orient][name];
      b.querySelector('[data-dims]').textContent = `${w} × ${h} cm`;
      b.querySelector('[data-price]').textContent = this.fmtShort(this.pricePence(name));
      const v = this.variantFor(s.orient, name);
      b.classList.toggle('is-unavailable', !!(v && v.available === false));
    });

    const v = this.variantFor(s.orient, s.size);
    const big = s.sizeChosen ? this.fmtBig(this.pricePence(s.size)) : 'From ' + this.fmtShort(this.pricePence('Small'));
    r.price.textContent = big;
    r.barPrice.textContent = big;
    r.variantId.value = v ? String(v.id) : '';

    // Add to basket: label, progress and messages
    r.add.disabled = s.busy;
    r.addLabel.textContent = s.busy
      ? s.progress >= 0
        ? `${s.status} ${Math.round(s.progress * 100)}%`
        : s.status
      : this.cfg.addLabel || 'Add to basket';
    r.progress.hidden = !(s.busy && s.progress >= 0);
    r.progressBar.style.transform = `scaleX(${s.busy && s.progress >= 0 ? s.progress : 0})`;
    r.status.hidden = s.busy || !s.status;
    r.status.textContent = s.busy ? '' : s.status;
    r.error.hidden = !s.error;
    r.error.textContent = s.error;
  }

  set(patch, skipPersist) {
    // Any change the customer makes answers the last message, so it goes.
    if (this.state.error && !('error' in patch)) this.state.error = '';
    Object.assign(this.state, patch);
    this.render();
    if (!skipPersist) this.persistSoon();
  }

  setStatus(text) {
    this.state.status = text;
    this.state.error = '';
    this.render();
  }

  setError(text) {
    this.state.error = text;
    this.state.status = '';
    this.render();
  }

  setProgress(p, label) {
    this.state.progress = p;
    this.state.status = label;
    this.render();
  }

  /* ---------------------------------------------------------------- bind */

  bind() {
    const r = this.refs;
    const s = this.state;

    r.upload.addEventListener('click', () => this.pickFile());
    r.sample.addEventListener('click', () => this.useSample());
    r.fileInput.addEventListener('change', () => {
      const f = r.fileInput.files && r.fileInput.files[0];
      if (f) this.loadFile(f);
      r.fileInput.value = '';
    });

    r.viewProduct.addEventListener('click', () => this.set({ view: 'product' }));
    r.viewRoom.addEventListener('click', () => this.set({ view: 'room' }));
    r.orientLand.addEventListener('click', () => this.set({ orient: 'Landscape' }));
    r.orientPort.addEventListener('click', () => this.set({ orient: 'Portrait' }));
    r.capWhite.addEventListener('click', () => this.set({ cap: 'White' }));
    r.capBlack.addEventListener('click', () => this.set({ cap: 'Black' }));
    this.querySelectorAll('[data-size]').forEach((b) => b.addEventListener('click', () => this.set({ size: b.dataset.size, sizeChosen: true })));

    r.zoom.addEventListener('input', (e) => this.set({ zoom: Number(e.target.value) }, true));
    r.zoom.addEventListener('change', (e) => this.set({ zoom: Number(e.target.value) }));
    r.fit.addEventListener('click', () => this.set({ zoom: 1, fx: 0, fy: 0 }));

    // The empty board is a real drop target, so the copy is honest and a dragged file
    // cannot navigate the browser away from the page. The stage accepts drops when full too.
    r.empty.addEventListener('click', () => this.pickFile());
    r.stage.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (!s.dragging) this.set({ dragging: true }, true);
    });
    r.stage.addEventListener('dragleave', (e) => {
      e.preventDefault();
      this.set({ dragging: false }, true);
    });
    r.stage.addEventListener('drop', (e) => {
      e.preventDefault();
      this.set({ dragging: false }, true);
      const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f && /^image\//.test(f.type)) this.loadFile(f);
    });

    r.clip.addEventListener('pointerdown', (e) => this.onDown(e));
    r.clip.addEventListener('keydown', (e) => this.onKey(e));
    r.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.addToBasket();
    });
  }

  pickFile() {
    this.refs.fileInput.click();
  }

  useSample() {
    const samples = this.cfg.samples || [];
    if (!samples.length) return;
    const i = this.state.si % samples.length;
    this.state.si += 1;
    this.state.file = null;
    idbDel().catch(() => {});
    this.load(samples[i], { kind: 'sample', index: i, name: '' });
  }

  loadFile(file) {
    this.state.file = file;
    idbPut(file).catch(() => {});
    this.load(URL.createObjectURL(file), { kind: 'file', index: -1, name: file.name });
  }

  load(url, meta, crop) {
    const im = new Image();
    // Artwork reopened from a basket line comes from the CDN, not a blob: URL.
    if (/^https?:/i.test(url) && url.indexOf(window.location.origin) !== 0) im.crossOrigin = 'anonymous';
    im.onload = () => {
      const nw = im.naturalWidth;
      const nh = im.naturalHeight;
      this.makePreview(im, nw, nh).then((previewUrl) => {
        const s = this.state;
        if (s.src && s.src.startsWith('blob:') && s.src !== previewUrl) URL.revokeObjectURL(s.src);
        if (url.startsWith('blob:') && url !== previewUrl) URL.revokeObjectURL(url);
        s.src = previewUrl;
        s.nw = nw;
        s.nh = nh;
        s.zoom = crop ? crop.zoom : 1;
        s.fx = crop ? crop.fx : 0;
        s.fy = crop ? crop.fy : 0;
        s.srcKind = meta.kind;
        s.srcIndex = meta.index;
        s.srcName = meta.name || '';
        s.error = '';
        s.status = '';
        this.render();
        this.persistSoon();
      });
    };
    im.onerror = () => {
      this.setError('That file could not be read in the browser. Upload a JPEG or PNG for the preview, or send us the file by email and we will place it for you.');
    };
    im.src = url;
  }

  // The preview is capped so a 25 MB original does not sit in memory as a full-size background;
  // the resolution check and the upload still use the original dimensions and file.
  makePreview(im, nw, nh) {
    const scale = Math.min(1, PREVIEW_MAX / Math.max(nw, nh));
    if (scale === 1) return Promise.resolve(im.src);
    const c = document.createElement('canvas');
    c.width = Math.round(nw * scale);
    c.height = Math.round(nh * scale);
    c.getContext('2d').drawImage(im, 0, 0, c.width, c.height);
    return new Promise((resolve) => {
      // A board reopened from the basket loads its artwork from Shopify's CDN. If that image
      // taints the canvas, toBlob throws; the full-size image is then used as its own preview
      // rather than the load failing silently.
      try {
        c.toBlob((b) => resolve(b ? URL.createObjectURL(b) : im.src), 'image/jpeg', 0.9);
      } catch (e) {
        resolve(im.src);
      }
    });
  }

  onDown(ev) {
    const s = this.state;
    if (!s.src) return;
    const g = this.geom();
    const el = ev.currentTarget;
    const r = el.getBoundingClientRect();
    const hox = ((g.dw - 100) / 200) * r.width;
    const hoy = ((g.dh - 100) / 200) * r.height;
    const start = { x: ev.clientX, y: ev.clientY, fx: s.fx, fy: s.fy };
    el.setPointerCapture(ev.pointerId);
    ev.preventDefault();
    const move = (mv) => {
      const nfx = hox > 0.5 ? start.fx + (mv.clientX - start.x) / hox : 0;
      const nfy = hoy > 0.5 ? start.fy + (mv.clientY - start.y) / hoy : 0;
      this.set({ fx: clamp(nfx), fy: clamp(nfy) }, true);
    };
    const up = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      this.persistSoon();
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  }

  // Keyboard: arrows move the focal point, + and - zoom, Home refits.
  onKey(ev) {
    const s = this.state;
    if (!s.src) return;
    const step = 0.05;
    const map = {
      ArrowLeft: { fx: clamp(s.fx - step) },
      ArrowRight: { fx: clamp(s.fx + step) },
      ArrowUp: { fy: clamp(s.fy - step) },
      ArrowDown: { fy: clamp(s.fy + step) },
      '+': { zoom: Math.min(3, +(s.zoom + 0.1).toFixed(2)) },
      '=': { zoom: Math.min(3, +(s.zoom + 0.1).toFixed(2)) },
      '-': { zoom: Math.max(1, +(s.zoom - 0.1).toFixed(2)) },
      Home: { zoom: 1, fx: 0, fy: 0 },
    };
    if (map[ev.key]) {
      ev.preventDefault();
      this.set(map[ev.key]);
    }
  }

  /* ------------------------------------------------------------ persist */

  persistSoon() {
    clearTimeout(this.persistTimer);
    this.persistTimer = setTimeout(() => this.persist(), 150);
  }

  persist() {
    const s = this.state;
    const data = {
      orient: s.orient,
      size: s.size,
      sizeChosen: s.sizeChosen,
      cap: s.cap,
      view: s.view,
      zoom: s.zoom,
      fx: s.fx,
      fy: s.fy,
      si: s.si,
      srcKind: s.srcKind,
      srcIndex: s.srcIndex,
      srcName: s.srcName,
    };
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(data));
    } catch (e) {
      /* private mode or full: the tool still works for this page view */
    }
  }

  // Reopened from the basket: the line carries the whole board in its _crop property, and the
  // artwork either sits in this browser's IndexedDB (same device, most recent board) or on
  // Shopify's CDN under the original-file property. Returns false if there is nothing usable, so the caller
  // falls back to the ordinary session restore.
  async restoreFromLine(line) {
    const props = (line && line.properties) || {};
    let crop = null;
    try {
      crop = JSON.parse(props._crop || 'null');
    } catch (e) {
      crop = null;
    }
    if (!crop) return false;

    const s = this.state;
    if (ORIENTS.includes(crop.orientation)) s.orient = crop.orientation;
    if (SIZES.includes(crop.size)) Object.assign(s, { size: crop.size, sizeChosen: true });
    if (crop.cap === 'White' || crop.cap === 'Black') s.cap = crop.cap;
    this.render();

    const view = {
      zoom: Math.min(3, Math.max(1, Number(crop.zoom) || 1)),
      fx: clamp(Number(crop.fx) || 0),
      fy: clamp(Number(crop.fy) || 0),
    };
    const name = crop.file && crop.file !== 'sample photograph' ? crop.file : 'artwork';

    const blob = await idbGet().catch(() => null);
    if (blob) {
      s.file = blob;
      this.load(URL.createObjectURL(blob), { kind: 'file', index: -1, name: blob.name || name }, view);
      return true;
    }

    const url = artworkUrl(props);
    if (/^https?:/i.test(url)) {
      // No original file in this browser, so the board is rebuilt from the uploaded copy. The
      // add that follows re-attaches it by URL rather than re-uploading.
      this.load(url, { kind: 'file', index: -1, name: name }, view);
      return true;
    }
    return false;
  }

  async restore() {
    const editing = window.btfEditLine;
    if (editing && editing.active) {
      const line = await editing.ready;
      if (line && (await this.restoreFromLine(line))) {
        this.setStatus('Amend your board, then add it back to your basket.');
        return;
      }
    }
    if (this.restoreShared()) return;

    let data = null;
    try {
      data = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
    } catch (e) {
      data = null;
    }
    if (!data) return;
    const s = this.state;
    if (ORIENTS.includes(data.orient)) s.orient = data.orient;
    if (SIZES.includes(data.size)) s.size = data.size;
    s.sizeChosen = !!data.sizeChosen;
    if (data.cap === 'White' || data.cap === 'Black') s.cap = data.cap;
    if (data.view === 'product' || data.view === 'room') s.view = data.view;
    s.si = Number(data.si) || 0;
    this.render();

    const crop = {
      zoom: Math.min(3, Math.max(1, Number(data.zoom) || 1)),
      fx: clamp(Number(data.fx) || 0),
      fy: clamp(Number(data.fy) || 0),
    };
    const samples = this.cfg.samples || [];
    if (data.srcKind === 'sample' && samples[data.srcIndex]) {
      this.load(samples[data.srcIndex], { kind: 'sample', index: data.srcIndex, name: '' }, crop);
    } else if (data.srcKind === 'file') {
      const blob = await idbGet().catch(() => null);
      if (blob) {
        s.file = blob;
        this.load(URL.createObjectURL(blob), { kind: 'file', index: -1, name: data.srcName || (blob.name || 'artwork') }, crop);
      }
    }
  }

  /* ------------------------------------------------------------- sharing */

  // A shared link (assets/btf-share.js) carries the format, size and caps, and for a sample
  // photograph its number and crop. A customer's own photograph cannot travel in a link.
  restoreShared() {
    const q = new URLSearchParams(window.location.search);
    if (q.has('edit') || !['orient', 'size', 'caps', 'sample'].some((k) => q.has(k))) return false;
    const s = this.state;
    const cap = (v) => (v ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : v);
    if (ORIENTS.includes(cap(q.get('orient')))) s.orient = cap(q.get('orient'));
    if (SIZES.includes(cap(q.get('size')))) Object.assign(s, { size: cap(q.get('size')), sizeChosen: true });
    if (q.get('caps') === 'black' || q.get('caps') === 'white') s.cap = cap(q.get('caps'));
    this.render();
    const samples = this.cfg.samples || [];
    const i = parseInt(q.get('sample') || '', 10);
    if (samples[i]) {
      s.si = i + 1;
      this.load(samples[i], { kind: 'sample', index: i, name: '' }, {
        zoom: Math.min(3, Math.max(1, Number(q.get('zoom')) || 1)),
        fx: clamp(Number(q.get('fx')) || 0),
        fy: clamp(Number(q.get('fy')) || 0),
      });
    }
    return true;
  }

  // "Send it to someone before you decide", shown once there is a photograph on the board
  shareStrip() {
    const share = this.querySelector('btf-share');
    if (!share) return;
    if (!share.provider) {
      const wrap = share.closest('.btf-cyo__share');
      const sub = share.querySelector('.btf-share__sub');
      this.shareSub = sub ? sub.textContent : '';
      share.provider = {
        picture: () => (this.state.src ? this.makeThumb(1200) : null),
        rooms: () => this.roomPictures(),
        params: () => {
          const s = this.state;
          const p = new URLSearchParams();
          p.set('orient', s.orient.toLowerCase());
          if (s.sizeChosen) p.set('size', s.size.toLowerCase());
          p.set('caps', s.cap.toLowerCase());
          if (s.srcKind === 'sample' && s.srcIndex >= 0) {
            p.set('sample', String(s.srcIndex));
            p.set('zoom', String(+s.zoom.toFixed(3)));
            p.set('fx', String(+s.fx.toFixed(3)));
            p.set('fy', String(+s.fy.toFixed(3)));
          }
          return p;
        },
        tokens: () => {
          const s = this.state;
          return {
            size: s.size + ' · ' + s.orient.toLowerCase(),
            price: this.fmtShort(this.pricePence(s.size)),
          };
        },
      };
      this.shareOwn = wrap ? { sub: wrap.dataset.subOwn, message: wrap.dataset.messageOwn } : {};
    }
    // The strip reads its settings when its script arrives, which may be after this first runs
    if (this.shareMessage === undefined && share.cfg) this.shareMessage = share.cfg.message;
    const own = this.state.srcKind === 'file';
    share.hidden = !this.state.src;
    const sub = share.querySelector('.btf-share__sub');
    if (sub) sub.textContent = own && this.shareOwn.sub ? this.shareOwn.sub : this.shareSub;
    if (share.cfg && this.shareMessage !== undefined) {
      share.cfg.message = own && this.shareOwn.message ? this.shareOwn.message : this.shareMessage;
    }
  }

  /* -------------------------------------------------------------- upload */

  /*
   * The board on the room plate, for the share strip (assets/btf-share.js): the photograph in the
   * board's space on the room view, the room multiplied over it and the caps, as the stage draws
   * them. Returns [{ key, label, canvas, focus }].
   */
  async roomPictures() {
    const s = this.state;
    if (!s.src) return [];
    const view = s.view;
    s.view = 'room';
    const g = this.geom();
    s.view = view;
    const plates = this.cfg.plates || {};
    const P = await import((window.btfAssets && window.btfAssets.picture) || './btf-picture.js');
    const S = 1600;
    const c = P.canvas(S, S);
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, S, S);
    const [l, t, w, h] = g.rect.map((v) => (v / 100) * S);
    const photo = await P.loadImage(s.src);
    ctx.save();
    P.roundedPath(ctx, l, t, w, h, (w * 3.5) / g.wcm, (h * 3.5) / g.hcm);
    ctx.clip();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(l, t, w, h);
    ctx.drawImage(photo, l + (g.left / 100) * w, t + (g.top / 100) * h, (g.dw / 100) * w, (g.dh / 100) * h);
    ctx.restore();
    const room = plates['room-' + g.k + '-room'];
    if (room) {
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';
      ctx.drawImage(await P.loadImage(room), 0, 0, S, S);
      ctx.restore();
    }
    const caps = plates['room-' + g.k + '-caps-' + g.cap];
    if (caps) ctx.drawImage(await P.loadImage(caps), 0, 0, S, S);
    return [{ key: 'room', label: 'In a room', canvas: c, focus: { x: (l + w / 2) / S, y: (t + h / 2) / S } }];
  }

  // The crop the customer sees, as a small JPEG for the visible "Your artwork" property.
  makeThumb(maxW) {
    const s = this.state;
    const g = this.geom();
    return new Promise((resolve) => {
      if (!s.src) return resolve(null);
      const im = new Image();
      im.onload = () => {
        try {
          const w = maxW;
          const h = Math.round(maxW / (g.wcm / g.hcm));
          const c = document.createElement('canvas');
          c.width = w;
          c.height = h;
          const x = c.getContext('2d');
          x.fillStyle = '#fff';
          x.fillRect(0, 0, w, h);
          x.drawImage(im, (g.left / 100) * w, (g.top / 100) * h, (g.dw / 100) * w, (g.dh / 100) * h);
          c.toBlob((b) => resolve(b), 'image/jpeg', 0.82);
        } catch (e) {
          resolve(null);
        }
      };
      im.onerror = () => resolve(null);
      im.src = s.src;
    });
  }

  blobToDataUrl(blob) {
    return new Promise((resolve) => {
      if (!blob) return resolve('');
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result || ''));
      fr.onerror = () => resolve('');
      fr.readAsDataURL(blob);
    });
  }

  // The board plus 5 mm bleed at 150 dpi, from the full-size photo, placed exactly as on screen
  async makePrintFile(PJ, g, progress) {
    const s = this.state;
    const bx = 500 / g.wmm;
    const by = 500 / g.hmm;
    const [W, H] = PJ.printPixels(g.bwmm, g.bhmm);
    const kx = W / (100 + 2 * bx);
    const ky = H / (100 + 2 * by);
    const x = (g.left + bx) * kx;
    const y = (g.top + by) * ky;
    const w = g.dw * kx;
    const h = g.dh * ky;
    const source = s.srcKind === 'sample' ? (this.cfg.samples || [])[s.srcIndex] : s.file || s.src;
    if (!source) throw new Error('no photo');
    const bmp = await PJ.loadBitmap(source, [w, h]);
    try {
      const out = await PJ.printJpeg({
        wmm: g.bwmm,
        hmm: g.bhmm,
        progress,
        draw: (ctx, y0) => {
          ctx.translate(0, -y0);
          PJ.drawPhoto(ctx, bmp, x, y, w, h, W, H);
        },
      });
      return out.blob;
    } finally {
      if (bmp.close) bmp.close();
    }
  }

  cropJson(g) {
    const s = this.state;
    // Source-pixel boxes: the keep is the visible board, the bleed box runs 5 mm past every edge.
    const toX = (p) => Math.round(((p - g.left) / g.dw) * s.nw);
    const toY = (p) => Math.round(((p - g.top) / g.dh) * s.nh);
    const bx = 500 / g.wmm;
    const by = 500 / g.hmm;
    return JSON.stringify({
      zoom: +s.zoom.toFixed(3),
      fx: +s.fx.toFixed(4),
      fy: +s.fy.toFixed(4),
      orientation: s.orient,
      size: s.size,
      cap: s.cap,
      cm: [g.wcm, g.hcm],
      print_mm: [g.bwmm, g.bhmm],
      src_px: [s.nw, s.nh],
      keep_px: [toX(0), toY(0), toX(100) - toX(0), toY(100) - toY(0)],
      bleed_px: [toX(-bx), toY(-by), toX(100 + bx) - toX(-bx), toY(100 + by) - toY(-by)],
      dpi: Math.round(g.dpi),
      file: s.srcKind === 'sample' ? 'sample photograph' : s.srcName,
    });
  }

  // Storefront/Admin-shaped staged upload: request a signed target, POST the file straight to it.
  async stagedUpload(blob, name, onProgress) {
    const cfg = this.cfg;
    const query =
      'mutation btfStagedUpload($input: [StagedUploadInput!]!) { stagedUploadsCreate(input: $input) { stagedTargets { url resourceUrl parameters { name value } } userErrors { field message } } }';
    const res = await fetch(cfg.uploadEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg.token,
      },
      body: JSON.stringify({
        query,
        variables: {
          input: [{ resource: 'FILE', filename: name, mimeType: blob.type || 'image/jpeg', fileSize: String(blob.size), httpMethod: 'POST' }],
        },
      }),
    });
    const j = await res.json();
    const data = j && j.data && j.data.stagedUploadsCreate;
    const target = data && data.stagedTargets && data.stagedTargets[0];
    if (!target || (j.errors && j.errors.length) || (data.userErrors && data.userErrors.length)) {
      const msg = (j.errors || data.userErrors || []).map((e) => e.message).join('; ');
      throw new Error(msg || 'No staged upload target returned');
    }
    const fd = new FormData();
    (target.parameters || []).forEach((p) => fd.append(p.name, p.value));
    fd.append('file', blob, name);
    const up = await postForm(target.url, fd, onProgress);
    if (up.status < 200 || up.status >= 300) throw new Error('Staged upload failed with HTTP ' + up.status);
    return target.resourceUrl;
  }

  cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
  }

  baseForm(v, g, flag, approved) {
    const fd = new FormData();
    fd.append('id', String(v.id));
    fd.append('quantity', '1');
    if (approved) fd.append(`properties[${APPROVED}]`, approved);
    const ids = this.cartSectionIds();
    if (ids.length) fd.append('sections', ids.join(','));
    fd.append('properties[_crop]', this.cropJson(g));
    // Where this board was configured, so the basket can offer to reopen it.
    fd.append('properties[_edit_url]', window.location.pathname);
    // The screw caps are a real production choice, so the workshop sees them on the order
    // rather than only inside the hidden crop.
    fd.append('properties[Screw caps]', this.state.cap);
    if (flag) fd.append('properties[_Resolution]', flag);
    const words = readable(g, this.state);
    fd.append('properties[_Print size]', words.size);
    if (words.crop && this.state.srcKind === 'file') fd.append('properties[_Crop from original]', words.crop);
    if (this.state.srcKind === 'file' && this.state.srcName) fd.append('properties[_Original file name]', this.state.srcName);
    return fd;
  }

  async addToBasket() {
    const s = this.state;
    const g = this.geom();
    if (s.busy) return;
    if (!g.has) {
      this.setStatus('Choose a photograph first.');
      this.pickFile();
      return;
    }
    if (!s.sizeChosen) {
      this.setStatus('Choose a size first.');
      const first = this.querySelector('[data-size]');
      if (first) {
        first.closest('fieldset, .btf-cyo__opt--size, div')?.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        first.focus({ preventScroll: true });
      }
      return;
    }
    const v = this.variantFor(s.orient, s.size);
    if (!v) {
      this.setError('This size is not set up in the shop yet. Please contact us and we will sort it out.');
      return;
    }
    if (v.available === false) {
      this.setError('This size is not available at the moment.');
      return;
    }
    if (g.tier === 'bad') {
      this.setError(this.tooSmall(g));
      return;
    }

    const PJ = await printJpegModule();
    const approved = PJ.approval(this);
    if (approved === null) return;

    s.busy = true;
    s.error = '';
    this.setProgress(-1, 'Preparing your file…');

    // Under 150 dpi the print file is enlarged to 150; the flag says how far under.
    const flag =
      g.tier === 'good'
        ? ''
        : `${g.tier === 'fair' ? 'Below 150 dpi' : 'LOW'} — the photo is ${Math.round(g.dpi)} dpi at print size, so the print file is enlarged to 150 dpi. The crop is ${n(g.cropW)} × ${n(g.cropH)} px; 150 dpi needs ${n(g.reqW)} × ${n(g.reqH)} px. Enhance before printing.`;

    // The print file: the board plus bleed at 150 dpi, cropped exactly as the customer sees it
    let printFile = null;
    let printNote = '';
    try {
      this.setProgress(0, 'Making your print file…');
      printFile = await this.makePrintFile(PJ, g, (p) => this.setProgress(p, 'Making your print file…'));
    } catch (e) {
      console.warn('[btf-cyo] print file not made:', e);
      printNote = `yes — the print file could not be made in the customer's browser (${(e && e.message) || e}); make it from the original and the crop`;
    }
    const printName = `print-${s.size}-${s.orient}-${g.bwmm}x${g.bhmm}mm-150dpi.jpg`.toLowerCase();

    const maxMb = Number(this.cfg.maxMb) || 20;
    let artwork = null; // URL string or Blob
    let thumb = null; // URL string or Blob
    let pending = '';
    let thumbBlob = null;

    if (s.srcKind === 'sample') {
      artwork = (this.cfg.samples || [])[s.srcIndex] || '';
      thumb = artwork;
    } else {
      thumbBlob = await this.makeThumb(1200);
      if (!s.file) {
        pending = 'yes — the original file was not attached (the browser session was refreshed); ask the customer for it';
        thumb = thumbBlob;
      } else {
        const name = s.srcName || 'artwork.jpg';
        // 1. Staged upload, when the merchant has filled in the access token.
        if (this.cfg.token && this.cfg.uploadEndpoint) {
          try {
            this.setProgress(0, 'Uploading your file…');
            artwork = await this.stagedUpload(s.file, name, (p) => this.setProgress(p, 'Uploading your file…'));
            if (thumbBlob && !printFile) thumb = await this.stagedUpload(thumbBlob, 'your-artwork.jpg');
            if (printFile) printFile = await this.stagedUpload(printFile, printName);
          } catch (e) {
            console.warn('[btf-cyo] staged upload unavailable, using the direct cart upload instead:', e);
            artwork = null;
            thumb = null;
          }
        }
        // 2. Shopify's own file line-item properties: the file posts with the cart line.
        if (!artwork) {
          if (s.file.size <= maxMb * 1048576) {
            artwork = s.file;
          } else {
            pending = `yes — the file is ${(s.file.size / 1048576).toFixed(1)} MB, over the ${maxMb} MB direct-upload limit; ask the customer for the original`;
          }
        }
        if (!thumb) thumb = thumbBlob;
      }
    }
    // With the artwork attached, the basket and the order show it; the small picture is only
    // for a line whose artwork could not be made
    if (printFile) thumb = null;

    const fd = this.baseForm(v, g, flag, approved);
    const fileName = s.srcName || 'artwork.jpg';
    if (printFile instanceof Blob) fd.append(`properties[${PRINT_FILE}]`, printFile, printName);
    else if (printFile) fd.append(`properties[${PRINT_FILE}]`, printFile);
    if (printNote) fd.append('properties[_Print file missing]', printNote);
    if (artwork instanceof Blob) fd.append(`properties[${ORIGINAL}]`, artwork, fileName);
    else if (artwork) fd.append(`properties[${ORIGINAL}]`, artwork);
    if (thumb instanceof Blob) fd.append('properties[Your artwork]', thumb, 'your-artwork.jpg');
    else if (thumb) fd.append('properties[Your artwork]', thumb);
    if (pending) fd.append('properties[_File missing]', pending);

    const usedFiles = artwork instanceof Blob || thumb instanceof Blob || printFile instanceof Blob;
    this.setProgress(usedFiles ? 0 : -1, usedFiles ? 'Uploading your file…' : 'Adding to basket…');

    let res;
    try {
      res = await postForm(this.cfg.cartAddUrl, fd, usedFiles ? (p) => this.setProgress(p, 'Uploading your file…') : null);
    } catch (e) {
      res = { status: 0, json: null, error: e };
    }

    const ok = (x) => x && x.status === 200 && x.json && !x.json.status;

    // 3. Too much to send at once: the print file and proof without the backup original.
    if (!ok(res) && printFile instanceof Blob && artwork instanceof Blob) {
      const fd1 = this.baseForm(v, g, flag, approved);
      fd1.append(`properties[${PRINT_FILE}]`, printFile, printName);
      fd1.append('properties[_File missing]', "the customer's original was not attached (the upload was too large); the print file is complete");
      this.setProgress(0, 'Uploading your file…');
      try {
        res = await postForm(this.cfg.cartAddUrl, fd1, (p) => this.setProgress(p, 'Uploading your file…'));
      } catch (e) {
        res = { status: 0, json: null, error: e };
      }
    }

    // 4. Last resort: keep the order moving with a small data-URL preview and flag the studio.
    if (!ok(res) && usedFiles) {
      const reason = (res.json && (res.json.description || res.json.message)) || (res.error && res.error.message) || 'HTTP ' + res.status;
      const fd2 = this.baseForm(v, g, flag, approved);
      const dataUrl = await this.blobToDataUrl(await this.makeThumb(320));
      fd2.append('properties[Your artwork]', dataUrl || 'preview unavailable');
      fd2.append('properties[_File missing]', `yes — direct upload failed (${reason}); ask the customer for the original file`);
      this.setProgress(-1, 'Adding to basket…');
      try {
        res = await postForm(this.cfg.cartAddUrl, fd2, null);
      } catch (e) {
        res = { status: 0, json: null, error: e };
      }
    }

    if (ok(res)) {
      await tidyLine(res.json, this.cfg.cartChangeUrl || '/cart/change.js');
      // Editing an existing line: the original goes only after its replacement is in, so a
      // failed add never costs the customer the board they already had.
      const editing = window.btfEditLine;
      if (editing && editing.active) await editing.replaceOriginal();
      await this.announce(res.json, v);
      s.busy = false;
      s.progress = -1;
      this.setStatus(editing && editing.line ? 'Your basket is updated.' : 'Added to your basket.');
      // A personalised board goes straight to the basket so the crop and size can be checked,
      // rather than leaving the customer on the tool unsure whether it worked.
      const basket = (this.cfg && this.cfg.cartUrl) || '/cart';
      window.location.assign(basket);
      return;
    } else {
      s.busy = false;
      s.progress = -1;
      const reason = (res.json && (res.json.description || res.json.message)) || (res.error && res.error.message) || '';
      this.setError(reason ? `${reason} Nothing was lost — try again.` : 'The upload did not complete. Check your connection and try again; your crop is kept.');
    }
  }

  // Tell Horizon: the header count and the cart drawer listen for this event.
  async announce(response, v) {
    let events = null;
    try {
      events = await import('@shopify/events');
    } catch (e) {
      events = null;
    }
    const CartLinesUpdateEvent = events && events.CartLinesUpdateEvent;
    if (!CartLinesUpdateEvent) return;
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(v.id), quantity: 1 }],
        promise: deferred.promise,
      })
    );
    try {
      const cartItems = document.querySelector('cart-items-component');
      let ajaxCart;
      if (cartItems) {
        await customElements.whenDefined('cart-items-component');
        ajaxCart = await cartItems.fetchCartData();
      } else {
        ajaxCart = await fetch(this.cfg.cartUrl + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'product-form-component',
          sourceId: this.id || 'btf-cyo-tool',
          itemCount: 1,
          productId: String(v.productId || ''),
          sections: response.sections,
          didError: false,
        },
      });
    } catch (e) {
      deferred.reject(e);
    }
  }
}

if (!customElements.get('btf-cyo-tool')) customElements.define('btf-cyo-tool', BtfCyoTool);
